import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Grid,
  HeaderTemplate,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import { AxiosError } from 'axios';
import { fallbackEmptyString, parseLocationSearch } from '../../../utils/StringUtils';
import RouteKey from '../../../routes/key';
import { PaymentLinkRequest } from '../../../api/candidate/exam/model';
import { candidateService, GenericErrorResponse } from '../../../api';
import ExamReviewSubjects from '../../examRegistration/components/ExamReviewSubjects';
import ExamFeeDetails from '../../../components/ExamFeeDetails';
import PaymentButton, { PaymentMethod } from '../../payment/components/PaymentButton';
import { useGetUserDetails } from '../../../api-refactored/user/useGetUserDetails';
import { selectIsUserLoggedIn } from '../../../store/appSlice';
import { GET_USER_DETAILS_QUERY_KEY } from '../../../constants/queryKeys';
import PersonalDetails from '../../examRegistration/components/particulars/PersonalDetails';
import ContactDetails from '../../examRegistration/components/particulars/ContactDetails';
import NextOfKinContactDetails from '../../examRegistration/components/particulars/NextOfKinContactDetails';
import ExamTypeUuidDisplay from '../../examRegistration/components/ExamTypeUuidDisplay';
import { usePostPaymentAccess } from '../../../api-refactored/financial/usePostPaymentAccess';

const { invalid, receipt, examRegistration } = RouteKey['payment-link'].child;
const { expired, used } = RouteKey['payment-link'].child;
const { paymentLink } = RouteKey.child;

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

interface ExamPathParams {
  uuid?: string;
}

interface ErrorMap {
  [key: string]: string | undefined;
}

const errorCallback = (err: AxiosError<GenericErrorResponse>, history: any) => {
  const errorCode = err?.response?.data?.code;
  if (errorCode) {
    const errorMapping: ErrorMap = {
      '1013': invalid,
      '1014': expired,
      '1017': used,
      '1019': invalid,
      '5004': expired,
    };

    const errorType = errorMapping[errorCode] as string;
    if (errorType) {
      history.push(`/${paymentLink}/${examRegistration}/${errorType}`);
    }
  }
};

const ExamRegistrationPaymentLink = () => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const queryClient = useQueryClient();

  const history = useHistory();
  const location = useLocation<PaymentLinkRequest>();
  const { token } = parseLocationSearch(location.search);
  const { uuid = '' } = useParams<ExamPathParams>();

  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  const { isLoading: isLoadingUserDetails } = useGetUserDetails();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);

  useEffect(() => {
    if (!token || !uuid) {
      history.push(`/${paymentLink}/${examRegistration}/${invalid}`);
    }
  }, [token, history, uuid]);

  const { mutate: mutatePostPaymentAccess } = usePostPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const getPaymentAccessDetails = () =>
    mutatePostPaymentAccess({
      registrationUuid: uuid,
    });

  const {
    isError,
    mutate: examRegistrationMutate,
    isLoading: examRegistrationProfileLoading,
    data: examReviewData,
  } = candidateService.exam.candidate.usePostExamRegistrationCandidateProfile(
    uuid,
    () => getPaymentAccessDetails(),
    (err) => {
      errorCallback(err, history);
    }
  );

  const invalidSessionCallback = () => {
    examRegistrationMutate({ token });
    queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
    return false;
  };

  // Call this API one time to set new cookie.
  // Backend will set cookie to unique link user (note it's not the same as logged out user).
  // In success and fail callbacks, refresh user log in status
  const {
    mutate: invalidateSessionMutate,
    isLoading: isLoadingInvalidateSession,
  } = candidateService.exam.candidate.usePostExamRegistrationCandidateProfile(
    uuid,
    () => getPaymentAccessDetails(),
    (err) => {
      errorCallback(err, history);
    },
    invalidSessionCallback
  );
  // ===========================================================================
  // Call validate unique link if there is no logged in user
  // ===========================================================================
  useEffect(() => {
    if (!isUserLoggedIn && !isLoadingUserDetails) {
      invalidateSessionMutate({ token });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isLoadingUserDetails, isUserLoggedIn, token]);

  /**
   * */
  const handleClickPaymentButtonTest = (callback: () => void) => {
    callback();
  };

  /** to save candidate after payment is successful
   * */
  const candidateExamPayment = candidateService.exam.candidate.usePostExamProceedToPayment();

  const handlePaymentOption = (
    callback: (internalTransactionRef: string) => void,
    method?: string
  ) => {
    candidateExamPayment.mutate(
      {
        uuid,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (res) => {
          if (res.data.paymentSuccess === true) {
            history.push(`/${paymentLink}/${examRegistration}/${uuid}/${receipt}`);
          }
          callback(res.data.internalTransactionRef);
        },
        onError: () => {
          callback('');
        },
      }
    );
  };

  if (isError) {
    return (
      <ContentTemplate>
        <Typography>An unexpected error occurred. Please refresh the page.</Typography>
      </ContentTemplate>
    );
  }

  if (isUserLoggedIn) {
    return (
      <ContentTemplate>
        <Typography variant="subtitle1">
          Authenticated session detected in the current browser. Please log out to proceed.
        </Typography>
        <Button onClick={() => invalidateSessionMutate({ token })}>Logout</Button>
      </ContentTemplate>
    );
  }

  if (isLoadingUserDetails || isLoadingInvalidateSession || examRegistrationProfileLoading) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <HeaderTemplate>
        <ExamTypeUuidDisplay />
      </HeaderTemplate>
      {examReviewData && (
        <Box>
          <ContentTemplate isContinuable type="full">
            <SectionHeader title={t('exam->>ReviewExam.Confirm Particulars')} />
            <PersonalDetails examCandidateDetail={examReviewData.data.candidateDetails} />
            <ContactDetails contactDetails={examReviewData.data.candidateDetails} />
            <NextOfKinContactDetails {...examReviewData.data.candidateDetails} />
          </ContentTemplate>

          <ContentTemplate isContinuable type="full">
            <ExamReviewSubjects {...examReviewData.data} />
            <ExamFeeDetails
              gstRegistrationNumber={examReviewData.data.gstRegistrationNumber}
              groupedLevelFees={examReviewData.data.groupedLevelFees}
              payment={examReviewData.data.payment}
            />
          </ContentTemplate>

          <ContentTemplate continuous>
            <Grid container direction="row" alignItems="flex-start" spacing={3}>
              <Grid xs={12} md={4}>
                <PaymentButton
                  buttonClassName={classes.paymentButton}
                  isUniqueLink={!!token}
                  disabled={isPaymentDisabled}
                  onPaymentComplete={(isRetry) => {
                    setIsPaymentDisabled(isRetry);
                  }}
                  paymentMethodsAvailable={paymentAccessDetailsOption}
                  onClickPaymentButton={(callback) => {
                    handleClickPaymentButtonTest(callback);
                  }}
                  onHandlePaymentMethod={(callback, method) => {
                    handlePaymentOption(callback, method);
                  }}
                  onSuccessPayment={() => {
                    history.push(`/${paymentLink}/${examRegistration}/${uuid}/${receipt}`);
                  }}
                  fullWidth
                  buttonText={t('exam->>ReviewExam.ConfirmAndPay')}
                  buttonVariant="contained"
                />
              </Grid>
            </Grid>
          </ContentTemplate>
        </Box>
      )}
    </>
  );
};
export default ExamRegistrationPaymentLink;
